import { Camera, type MediaResult } from '@capacitor/camera'
import { Filesystem } from '@capacitor/filesystem'

import { isNativePlatform } from './platform'

type NativeImage = {
  blob: Blob
  dataUrl: string
}

const mimeFromFormat = (format?: string): string => {
  const normalized = format?.toLowerCase()
  if (normalized === 'png') return 'image/png'
  if (normalized === 'gif') return 'image/gif'
  if (normalized === 'webp') return 'image/webp'
  return 'image/jpeg'
}

const base64ToBlob = (base64: string, mime: string): Blob => {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: mime })
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
      else reject(new Error('Failed to encode photo'))
    }
    reader.onerror = () => reject(reader.error ?? new Error('Failed to encode photo'))
    reader.readAsDataURL(blob)
  })

const mediaResultToImage = async (result: MediaResult): Promise<NativeImage | null> => {
  const mime = mimeFromFormat(result.metadata?.format)

  if (result.webPath) {
    const blob = await (await fetch(result.webPath)).blob()
    const typedBlob = blob.type ? blob : new Blob([blob], { type: mime })
    return { blob: typedBlob, dataUrl: await blobToDataUrl(typedBlob) }
  }

  if (result.uri) {
    const { data } = await Filesystem.readFile({ path: result.uri })
    if (typeof data !== 'string') return { blob: data, dataUrl: await blobToDataUrl(data) }
    const blob = base64ToBlob(data, mime)
    return { blob, dataUrl: `data:${mime};base64,${data}` }
  }

  return null
}

const takeNativeImage = async (): Promise<NativeImage | null> => {
  if (!isNativePlatform()) return null

  try {
    const result = await Camera.takePhoto({
      quality: 80,
      correctOrientation: true,
      includeMetadata: true,
    })
    return mediaResultToImage(result)
  } catch {
    return null
  }
}

export async function captureNativePhoto(): Promise<NativeImage | null> {
  return takeNativeImage()
}

export async function pickNativePhoto(): Promise<(NativeImage & { file: File }) | null> {
  const photo = await takeNativeImage()
  if (!photo) return null
  const file = new File([photo.blob], `photo-${Date.now()}.jpg`, { type: photo.blob.type || 'image/jpeg' })
  return { ...photo, file }
}
